import React, { useState, useEffect } from 'react';
import { Truck, Server, User, Wifi, CheckCircle2, AlertCircle, ArrowRight, RefreshCw, Smartphone, Globe } from 'lucide-react';

export default function DriverConnectPage({ onConnect, initialServerUrl = '', initialDriverId = '' }) {
  const defaultUrl = initialServerUrl || localStorage.getItem('loops_driver_server_url') || (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:${window.location.port || '5000'}` : 'http://localhost:5000');
  
  const [serverUrl, setServerUrl] = useState(defaultUrl);
  const [selectedDriverId, setSelectedDriverId] = useState(initialDriverId || localStorage.getItem('loops_driver_id') || '');
  const [drivers, setDrivers] = useState([]);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('');
  const [serverDetails, setServerDetails] = useState(null);

  // Clean server URL (remove trailing slash)
  const sanitizeUrl = (url) => {
    let clean = (url || '').trim();
    if (clean.endsWith('/')) clean = clean.slice(0, -1);
    if (!clean.startsWith('http://') && !clean.startsWith('https://')) {
      clean = 'http://' + clean;
    }
    return clean;
  };

  const testAndFetchDrivers = async (targetUrl = serverUrl) => {
    const cleanUrl = sanitizeUrl(targetUrl);
    setIsTesting(true);
    setConnectionStatus('testing');
    setStatusMessage('Connexion au serveur Loops en cours...');
    setDrivers([]);

    try {
      // 1. Check server status / bootstrap
      const response = await fetch(`${cleanUrl}/api/bootstrap?all=true`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Le serveur a répondu avec le code HTTP ${response.status}`);
      }

      const data = await response.json();
      const driverList = data.drivers || [];

      if (driverList.length === 0) {
        setConnectionStatus('error');
        setStatusMessage('Connecté au serveur, mais aucun chauffeur n\'est enregistré dans la base de données.');
        return;
      }

      setDrivers(driverList);
      setConnectionStatus('success');
      setStatusMessage(`Connecté avec succès ! (${driverList.length} chauffeur(s) disponible(s))`);
      setServerDetails({
        jobsCount: (data.jobs || []).length,
        trucksCount: (data.trucks || []).length
      });

      // Auto-select previous or first driver
      const savedId = localStorage.getItem('loops_driver_id');
      if (savedId && driverList.some(d => String(d.id) === String(savedId))) {
        setSelectedDriverId(savedId);
      } else if (driverList.length > 0) {
        setSelectedDriverId(String(driverList[0].id));
      }
    } catch (err) {
      console.error('Erreur de connexion au serveur Loops:', err);
      setConnectionStatus('error');
      setStatusMessage(`Impossible de joindre le serveur (${err.message}). Vérifiez l'adresse IP / URL et que le serveur est bien démarré.`);
    } finally {
      setIsTesting(false);
    }
  };

  // Attempt auto-connect on load if a valid URL exists
  useEffect(() => {
    if (defaultUrl) {
      testAndFetchDrivers(defaultUrl);
    }
  }, []);

  const handleLaunch = () => {
    const cleanUrl = sanitizeUrl(serverUrl);
    if (!selectedDriverId) {
      alert('Veuillez sélectionner un chauffeur pour continuer.');
      return;
    }

    localStorage.setItem('loops_driver_server_url', cleanUrl);
    localStorage.setItem('loops_driver_id', String(selectedDriverId));

    if (onConnect) {
      onConnect(String(selectedDriverId), cleanUrl);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #090d16 0%, #0f172a 100%)',
      color: '#f8fafc',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px',
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        {/* Header with App Logo */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px auto',
            boxShadow: '0 8px 20px rgba(56, 189, 248, 0.3)'
          }}>
            <Truck size={32} color="#0f172a" />
          </div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '800', margin: '0 0 4px 0', letterSpacing: '-0.5px' }}>
            Loops Chauffeur
          </h1>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>
            Configuration de la connexion au serveur de tournées
          </p>
        </div>

        {/* Step 1: Server URL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Server size={14} color="#38bdf8" />
            <span>Adresse du Serveur Loops</span>
          </label>

          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="ex: http://192.168.1.50:5000"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              style={{
                flex: 1,
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '8px',
                padding: '10px 12px',
                color: '#f8fafc',
                fontSize: '0.85rem',
                outline: 'none'
              }}
            />
            <button
              onClick={() => testAndFetchDrivers(serverUrl)}
              disabled={isTesting}
              style={{
                background: '#0284c7',
                border: 'none',
                borderRadius: '8px',
                padding: '0 14px',
                color: '#fff',
                fontWeight: '700',
                fontSize: '0.8rem',
                cursor: isTesting ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Tester la connexion"
            >
              <RefreshCw size={14} style={{ animation: isTesting ? 'spin 1s linear infinite' : 'none' }} />
              <span>{isTesting ? '...' : 'Tester'}</span>
            </button>
          </div>

          {/* Connection Status Banner */}
          {connectionStatus !== 'idle' && (
            <div style={{
              background: connectionStatus === 'success' ? 'rgba(34, 197, 94, 0.12)' : (connectionStatus === 'testing' ? 'rgba(56, 189, 248, 0.12)' : 'rgba(239, 68, 68, 0.12)'),
              border: `1px solid ${connectionStatus === 'success' ? '#22c55e' : (connectionStatus === 'testing' ? '#38bdf8' : '#ef4444')}`,
              borderRadius: '8px',
              padding: '8px 10px',
              fontSize: '0.72rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              color: connectionStatus === 'success' ? '#4ade80' : (connectionStatus === 'testing' ? '#38bdf8' : '#f87171')
            }}>
              {connectionStatus === 'success' ? <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: '1px' }} /> : (connectionStatus === 'testing' ? <Wifi size={16} style={{ flexShrink: 0, marginTop: '1px' }} /> : <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />)}
              <div style={{ lineHeight: '1.4' }}>{statusMessage}</div>
            </div>
          )}
        </div>

        {/* Step 2: Driver Picker */}
        {connectionStatus === 'success' && drivers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', animation: 'fadeIn 0.2s ease-in-out' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <User size={14} color="#38bdf8" />
              <span>Sélectionnez votre profil Chauffeur</span>
            </label>

            <select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              style={{
                background: '#0f172a',
                border: '1px solid #38bdf8',
                borderRadius: '8px',
                padding: '10px 12px',
                color: '#f8fafc',
                fontSize: '0.9rem',
                fontWeight: '700',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {drivers.map(d => (
                <option key={d.id} value={d.id} style={{ background: '#0f172a', color: '#fff' }}>
                  👤 {d.first_name} {d.last_name} {d.phone ? `(${d.phone})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleLaunch}
          disabled={connectionStatus !== 'success' || !selectedDriverId}
          style={{
            marginTop: '8px',
            background: (connectionStatus === 'success' && selectedDriverId) ? 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)' : '#334155',
            color: (connectionStatus === 'success' && selectedDriverId) ? '#0f172a' : '#64748b',
            border: 'none',
            borderRadius: '10px',
            padding: '13px',
            fontSize: '0.92rem',
            fontWeight: '800',
            cursor: (connectionStatus === 'success' && selectedDriverId) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: (connectionStatus === 'success' && selectedDriverId) ? '0 8px 24px rgba(56, 189, 248, 0.35)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <span>Accéder à mes tournées</span>
          <ArrowRight size={16} />
        </button>

        {/* Footer info */}
        <div style={{ textAlign: 'center', fontSize: '0.68rem', color: '#64748b', marginTop: '4px' }}>
          <span>Loops Driver App v1.0.0 Standalone · GPS & Télématique</span>
        </div>
      </div>
    </div>
  );
}
